import React, { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
	conversationKey: string;
	onSend: (prompt: string) => Promise<void>;
	disabled?: boolean;
	sending?: boolean;
}

const PromptComposer: React.FC<Props> = ({ conversationKey, onSend, disabled = false, sending = false }) => {
	const [prompt, setPrompt] = useState('');
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const sendingRef = useRef(sending);
	sendingRef.current = sending;

	useEffect(() => {
		setPrompt('');
	}, [conversationKey]);

	const handleSend = useCallback(() => {
		const trimmed = prompt.trim();
		if (!trimmed || disabled || sendingRef.current) return;

		onSend(trimmed)
			.then(() => setPrompt(''))
			.catch(() => undefined);
	}, [disabled, onSend, prompt]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			handleSend();
		}
	};

	return (
		<div className="ai-assistant__composer">
			<textarea
				ref={textareaRef}
				className="form-control ai-assistant__composer-input"
				placeholder="Ask Claude anything..."
				value={prompt}
				onChange={(event) => setPrompt(event.target.value)}
				onKeyDown={handleKeyDown}
				disabled={disabled || sending}
				rows={3}
			/>
			<div className="ai-assistant__composer-actions">
				<span className="ai-assistant__composer-hint">Press Enter to send, Shift+Enter for a new line</span>
				<button
					type="button"
					className="btn btn-primary ai-assistant__composer-send"
					onClick={handleSend}
					disabled={disabled || sending || !prompt.trim()}
				>
					<i className={`fa ${sending ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} />
					<span>{sending ? 'Thinking...' : 'Send'}</span>
				</button>
			</div>
			<p className="ai-assistant__composer-disclaimer">AI assistant can make mistakes. Check important info.</p>
		</div>
	);
};

export default PromptComposer;
